import { PackageRegistrationStatus, PackageTransferStatus } from "Common/Enums";

import { IMersData, IMersInitiators } from "Features/Vault/VaultInterfaces";

export interface DocListMersEligibilityContext {
  mersInitiators?: IMersInitiators;
  mersRegistrationStatus?: PackageRegistrationStatus;
  mersTransferStatus?: PackageTransferStatus;
  noteStatus?: string;
  originatorOrgId?: string;
  registrationStatusLabel?: string;
}

export interface AddDocumentEligibilityResult {
  eligible: boolean;
  reason?: string;
}

const NOT_REGISTERED_REASON = "This loan is not registered in MERS.";
const TRANSFER_PENDING_REASON = "A MERS transfer is pending for this loan.";
const NOTE_NOT_ACTIVE_REASON =
  "The MERS note is not Active and cannot receive Add Document transactions.";
const ORIGINATOR_NOT_AUTHORIZED_REASON =
  "The originator is not the Controller or SubServicer for this MERS registration and is not authorized to add documents.";

function isRegistered(context: DocListMersEligibilityContext): boolean | undefined {
  if (context.registrationStatusLabel !== undefined) {
    return context.registrationStatusLabel.trim().toLowerCase() === "registered";
  }

  if (context.mersRegistrationStatus !== undefined) {
    return context.mersRegistrationStatus === PackageRegistrationStatus.Registered;
  }

  return undefined;
}

function isOriginatorAuthorized(context: DocListMersEligibilityContext): boolean | undefined {
  const originatorOrgId = context.originatorOrgId?.trim();
  if (!originatorOrgId || !context.mersInitiators) {
    return undefined;
  }

  const controllerOrgId = context.mersInitiators.controller?.orgId?.trim();
  const subServicerOrgId = context.mersInitiators.subServicer?.orgId?.trim();

  return originatorOrgId === controllerOrgId || originatorOrgId === subServicerOrgId;
}

export function getAddDocumentEligibility(
  context?: DocListMersEligibilityContext
): AddDocumentEligibilityResult {
  if (!context) {
    return { eligible: true };
  }

  const registered = isRegistered(context);
  if (registered === false) {
    return { eligible: false, reason: NOT_REGISTERED_REASON };
  }

  if (
    context.mersTransferStatus !== undefined &&
    context.mersTransferStatus === PackageTransferStatus.TransferPending
  ) {
    return { eligible: false, reason: TRANSFER_PENDING_REASON };
  }

  const noteStatus = context.noteStatus?.trim();
  if (noteStatus && noteStatus.toLowerCase() !== "active") {
    return { eligible: false, reason: NOTE_NOT_ACTIVE_REASON };
  }

  const originatorAuthorized = isOriginatorAuthorized(context);
  if (originatorAuthorized === false) {
    return { eligible: false, reason: ORIGINATOR_NOT_AUTHORIZED_REASON };
  }

  return { eligible: true };
}

export function extractMersEligibilityFromRow(rowData: unknown): DocListMersEligibilityContext | undefined {
  if (!rowData || typeof rowData !== "object") {
    return undefined;
  }

  const row = rowData as Record<string, unknown>;

  if (row.mersData && typeof row.mersData === "object") {
    const mersData = row.mersData as IMersData;
    const context: DocListMersEligibilityContext = {
      mersInitiators: mersData.mersInitiators,
      mersRegistrationStatus: mersData.mersRegistrationStatus,
      mersTransferStatus: mersData.mersTransferStatus,
    };

    if (typeof row.originatorOrgId === "string") {
      context.originatorOrgId = row.originatorOrgId;
    }

    if (typeof row.noteStatus === "string") {
      context.noteStatus = row.noteStatus;
    }

    return context;
  }

  if (typeof row.registrationStatus === "string") {
    return { registrationStatusLabel: row.registrationStatus };
  }

  return undefined;
}
